/**
 * Floating Replay Button - Fixed position, context-aware replay button
 * 
 * Features:
 * - Fixed at bottom center of screen
 * - Gray without session, green with session
 * - Single tap with session: 30s clip (1x speed)
 * - Double tap with session: 60s clip (2x speed)
 * - Double tap without session: Navigate to QR scanner
 * - Uses ONLY API as source of truth for session state
 */
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, Check, Loader2, QrCode, Zap } from 'lucide-react';
import { useAuthStore } from '../store';
import api from '../services/api';
import { toast } from 'sonner';

const DOUBLE_TAP_DELAY = 300;

// Pages where floating button should be hidden
const HIDDEN_PATHS_EXACT = ['/watch', '/reloj', '/auth', '/login', '/'];
const HIDDEN_PATHS_PREFIX = ['/v/', '/share/', '/shared/', '/admin'];

export default function FloatingReplayButton() {
  const { user, isAuthenticated } = useAuthStore();
  const isPlayer = Boolean(user && ['player', 'player_free', 'player_premium'].includes(user.role));
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if we should hide on current path
  const shouldHide = 
    HIDDEN_PATHS_EXACT.includes(location.pathname) ||
    HIDDEN_PATHS_PREFIX.some(prefix => location.pathname.startsWith(prefix));
  
  const [activeSession, setActiveSession] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [clipType, setClipType] = useState(null);
  const [tapCount, setTapCount] = useState(0);
  const [showMessages, setShowMessages] = useState(true);
  const tapTimeoutRef = useRef(null);

  // Check for active session - ONLY use API as source of truth
  useEffect(() => {
    let isMounted = true;
    
    const checkSession = async () => {
      if (!isAuthenticated || !user) {
        setActiveSession(null);
        return;
      }

      try {
        const response = await api.get('/session');
        
        if (!isMounted) return;
        
        // Only set active if API returns valid court_id
        if (response.data && response.data.court_id) {
          let courtNumber = '?';
          if (response.data.club?.courts) {
            const court = response.data.club.courts.find(c => c.id === response.data.court_id);
            if (court) courtNumber = court.court_number;
          }
          if (response.data.court_number) {
            courtNumber = response.data.court_number;
          }
          
          setActiveSession({
            ...response.data,
            court_number: courtNumber
          });
        } else {
          // No active session
          setActiveSession(null);
        }
      } catch (err) {
        console.log('[FloatingButton] Session check error:', err.message);
        // On error, assume no session
        setActiveSession(null);
      }
    };

    // Check immediately
    checkSession();
    
    // Check every 10 seconds
    const interval = setInterval(checkSession, 10000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isAuthenticated, user, location.pathname]); // Re-check on route change

  // Create clip (only when session is active)
  const createClip = async (type) => {
    if (!activeSession || isLoading) return;

    setIsLoading(true);
    try {
      // type: 'normal' = 30s, 'fast' = 60s at 2x speed
      const isFast = type === 'fast';
      const response = await api.post('/clips/quick', {
        is_fast: isFast,
        speed_multiplier: isFast ? 2.0 : 1.0
      });

      if (response.data && response.data.clip_id) {
        setClipType(type);
        setShowSuccess(true);
        toast.success(isFast ? '¡Clip 60s solicitado!' : '¡Clip 30s solicitado!');
        
        setTimeout(() => {
          setShowSuccess(false);
          setClipType(null);
        }, 2000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Error al crear clip';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Navigate to QR scanner
  const goToQRScanner = () => {
    navigate('/scan');
    toast.info('Escanea el QR de la cancha para iniciar tu sesión');
  };

  // Handle tap with double-tap detection
  const handleTap = () => {
    if (isLoading) return;

    setTapCount(prev => prev + 1);

    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }

    tapTimeoutRef.current = setTimeout(() => {
      if (activeSession) {
        // Has session - create clips
        if (tapCount === 0) {
          createClip('normal');
        } else {
          // Double tap = 60s fast clip
          createClip('fast');
        }
      } else {
        // No session - double tap goes to QR scanner
        if (tapCount > 0) {
          goToQRScanner();
        } else {
          // Single tap without session - show hint
          toast.info('Toca 2 veces para escanear QR', { duration: 2000 });
        }
      }
      setTapCount(0);
    }, DOUBLE_TAP_DELAY);
  };

  // Track session state to detect changes
  const hasSession = !!activeSession;
  const prevHasSessionRef = useRef(hasSession);
  const timerRef = useRef(null);

  // Show messages initially and when session state changes, hide after 5 seconds
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // Check if session state actually changed
    const sessionStateChanged = prevHasSessionRef.current !== hasSession;
    prevHasSessionRef.current = hasSession;
    
    // Show messages on mount or when session state changes
    if (sessionStateChanged) {
      setShowMessages(true);
    }
    
    // Always set timer to hide after 5 seconds
    timerRef.current = setTimeout(() => {
      setShowMessages(false);
    }, 5000);
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [hasSession]);

  // Don't render if not authenticated or on hidden paths
  if (!isAuthenticated || !isPlayer || shouldHide) return null;

  return (
    <div className="fixed bottom-4 left-0 right-0 z-[9999] flex flex-col items-center pointer-events-none">
      {/* Messages Container */}
      <AnimatePresence>
        {showMessages && !hasSession && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="mb-3 space-y-2 pointer-events-auto"
          >
            {/* Message 1: Double tap hint */}
            <div className="bg-blue-500/90 backdrop-blur-sm text-white px-4 py-2 rounded-xl shadow-lg text-center">
              <p className="text-xs font-medium flex items-center justify-center gap-2">
                <Zap className="w-4 h-4" />
                Toca 2 veces para escanear QR
              </p>
            </div>
            
            {/* Message 2: Session invite */}
            <div className="bg-gray-800/90 backdrop-blur-sm text-gray-300 px-4 py-2 rounded-xl shadow-lg text-center max-w-[280px]">
              <p className="text-xs">
                📱 Inicia sesión en una cancha para hacer replay de tus jugadas
              </p>
            </div>
          </motion.div>
        )}

        {showMessages && hasSession && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="mb-3 pointer-events-auto"
          >
            <div className="bg-green-500/90 backdrop-blur-sm text-white px-4 py-2 rounded-xl shadow-lg text-center">
              <p className="text-xs font-medium">
                🏟️ Cancha {activeSession.court_number} • 1 tap = 30s • 2 taps = 60s 2x
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Button - Fixed position */}
      <motion.button
        className={`
          w-16 h-16 rounded-full shadow-2xl
          flex items-center justify-center
          transition-colors duration-300 pointer-events-auto
          active:scale-95
          ${isLoading ? 'opacity-70' : ''}
          ${hasSession 
            ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-green-500/30' 
            : 'bg-gradient-to-br from-gray-500 to-gray-600 shadow-gray-500/30'}
        `}
        onClick={handleTap}
        data-testid="floating-replay-button"
        whileTap={{ scale: 0.9 }}
        disabled={isLoading}
      >
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </motion.div>
          ) : showSuccess ? (
            <motion.div
              key="success"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="text-center"
            >
              <Check className="w-6 h-6 text-white mx-auto" />
              <span className="text-[8px] text-white">
                {clipType === 'fast' ? '60s 2x' : '30s'}
              </span>
            </motion.div>
          ) : (
            <motion.div
              key="ready"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="relative"
            >
              {hasSession ? (
                <Video className="w-8 h-8 text-white" />
              ) : (
                <QrCode className="w-8 h-8 text-white/70" />
              )}
              
              {/* Pulse animation when has session */}
              {hasSession && (
                <motion.div
                  className="absolute inset-0 rounded-full bg-white/20"
                  animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Tap indicator */}
      <AnimatePresence>
        {tapCount > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className="absolute bottom-16 right-1/2 translate-x-8 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-lg"
          >
            <Zap className="w-4 h-4 text-white" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
